import { type FC, useState, useEffect, useMemo } from "react";
import { Eye, EyeOff, Pencil } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { cn } from "@/shared/utils/core";
import { useEstateActivityLog } from "@/shared/hooks/useEstateActivityLog";
import { useItemDocumentReferences } from "@/shared/hooks/useItemDocumentReferences";
import { useItemBeneficiaries } from "@/shared/hooks/useItemBeneficiaries";
import { useUpdateBeneficiaries } from "@/shared/hooks/useUpdateBeneficiaries";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { OWNERSHIP_TYPES } from "@/shared/constants/ownershipTypes";
import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import { buildAddress, EMPTY_ADDRESS, toStateAbbreviation } from "@/shared/utils/addressUtils";
import { currencyInput } from "@/shared/utils/currencyInput";
import { isEstimatedItem, resolveBalance } from "@/shared/utils/balanceEstimates";
import { formatPersonDateForSave } from "@/shared/utils/formatters";
import { groupDebtsByType } from "@/shared/utils/estateMath";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { useAppContext } from "@/shared/hooks/useAppContext";

import type { EstateItem } from "@/shared/types/estateTypes";
import type { AddressParts } from "@/shared/utils/addressUtils";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";

import { useCanonicalItemAddress } from "@/shared/hooks/useCanonicalAddress";
import { getCategoryDetails } from "./categoryDetailFields";
import { ChangeItemTypeDialog } from "./ChangeItemTypeDialog";
import { ActivityLogSection } from "./ActivityLogSection";
import { RelatedDocsSection } from "./RelatedDocsSection";
import { BeneficiaryEditor } from "./BeneficiaryEditor";
import { AddValueSection } from "./AddValueSection";
import { BalanceEstimateSection } from "./BalanceEstimateSection";
import { LinkedMortgageSection, LinkedPropertySection } from "./LinkedItemSection";
import { SheetHeader } from "./SheetHeader";

interface EstateItemSheetProps {
  item: EstateItem | null;
  estateId: number | undefined;
  onClose: () => void;
  onUpdateItem?: (id: number, updates: Record<string, unknown>) => Promise<unknown>;
  onDeleteItem?: (id: number) => Promise<unknown>;
  onDocumentClick?: (doc: { id: number; name: string; createdAt: string }) => void;
  contacts?: import("@/shared/types/estateContact").PersonContact[];
  linkedSecuredDebts?: EstateItem[];
  linkedProperty?: EstateItem | null;
  onSelectItem?: (id: number) => void;
  isPaidUser?: boolean;
  onUpgrade?: () => void;
}

export const EstateItemSheet: FC<EstateItemSheetProps> = ({
  item,
  estateId,
  onClose,
  onUpdateItem,
  onDeleteItem,
  onDocumentClick,
  contacts: externalContacts,
  linkedSecuredDebts,
  linkedProperty,
  onSelectItem,
  isPaidUser = true,
  onUpgrade,
}) => {
  const { person } = useAppContext();
  const [visible, setVisible] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const [isChangeTypeOpen, setIsChangeTypeOpen] = useState(false);
  const [isChangingType, setIsChangingType] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, string | boolean>>({});
  const [addressDraft, setAddressDraft] = useState<AddressParts>(EMPTY_ADDRESS);
  const [addressEdited, setAddressEdited] = useState(false);

  useEffect(() => {
    if (item) {
      requestAnimationFrame(() => setVisible(true));
      document.body.style.overflow = "hidden";
    } else {
      setVisible(false);
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [item]);

  useEffect(() => {
    setIsEditing(false);
    setIdentifierMasked(true);
    setLocalShares({});
  }, [item?.id]);

  const { data: beneficiaries = [], isLoading: beneficiariesLoading } = useItemBeneficiaries(
    item?.id,
  );
  const { addBeneficiary, removeBeneficiary, updateBeneficiary } = useUpdateBeneficiaries();
  const [identifierMasked, setIdentifierMasked] = useState(true);
  const [localShares, setLocalShares] = useState<Record<number, string>>({});
  const beneficiaryContactIds = useMemo(
    () => beneficiaries.map((b) => b.contact_id),
    [beneficiaries],
  );
  const { data: docRefs = [], isLoading: docsLoading } = useItemDocumentReferences(
    beneficiariesLoading ? undefined : item?.id,
    beneficiaryContactIds.length > 0 ? beneficiaryContactIds : undefined,
  );
  const { data: activityData, isLoading: activityLoading } = useEstateActivityLog(
    estateId,
    item?.id,
  );

  const allEntries = useMemo(
    () => activityData?.pages.flatMap((p) => p.entries) ?? [],
    [activityData],
  );

  const { data: canonicalSitus, isFetched: isSitusFetched } = useCanonicalItemAddress(
    item?.category === "Properties" ? item.id : undefined,
  );
  const situsPending = item?.category === "Properties" && !isSitusFetched;
  const canonicalAddress = canonicalSitus
    ? buildAddress({
        streetAddress: canonicalSitus.street1,
        apartment: canonicalSitus.street2 ?? "",
        city: canonicalSitus.city ?? "",
        state: canonicalSitus.state ?? "",
        zipCode: canonicalSitus.zip,
      })
    : null;

  const details = item ? getCategoryDetails(item, canonicalAddress) : null;
  const hasValueField = details?.fields.some((f) => f.fieldType === "value") ?? false;
  const CATEGORIES_WITH_ESTIMATED_VALUE = ["Properties", "Vehicles"];
  const showBalanceEstimate =
    hasValueField && !CATEGORIES_WITH_ESTIMATED_VALUE.includes(item?.category ?? "");
  const needsValue = item
    ? item.balance === 0 && !isEstimatedItem(item) && !item.value_label
    : false;

  if (!item && !visible) return null;

  const handleEditStart = () => {
    if (!item) return;
    const meta = (item.metadata ?? {}) as Record<string, unknown>;
    const form: Record<string, string | boolean> = {
      balance: String(item.balance),
      ownership: item.ownership ?? "",
      last_updated_date:
        (item.last_updated_date ?? "").split("T")[0] || formatPersonDateForSave(new Date()) || "",
      is_estimated: item.is_estimated ?? false,
      balance_estimate_range: item.balance_estimate_range ?? "",
      item_type: item.item_type ?? "",
      account_last4: (item.identifiers?.account_last4 as string) ?? item.plaid_account_mask ?? "",
    };
    for (const [k, v] of Object.entries(meta)) {
      if (typeof v === "string") form[`meta_${k}`] = v;
    }
    if (item.category === "Properties") {
      setAddressDraft(
        canonicalSitus
          ? {
              streetAddress: canonicalSitus.street1,
              apartment: canonicalSitus.street2 ?? "",
              city: canonicalSitus.city ?? "",
              state: toStateAbbreviation(canonicalSitus.state ?? ""),
              zipCode: canonicalSitus.zip,
            }
          : {
              streetAddress: (meta.street_address as string) ?? "",
              apartment: (meta.apartment as string) ?? "",
              city: (meta.city as string) ?? "",
              state: toStateAbbreviation((meta.state as string) ?? ""),
              zipCode: (meta.zip_code as string) ?? "",
            },
      );
    } else {
      const addr = meta.address as AddressParts | undefined;
      setAddressDraft(addr ? { ...EMPTY_ADDRESS, ...addr } : EMPTY_ADDRESS);
    }
    setEditForm(form);
    setAddressEdited(false);
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  const handleEditSave = async () => {
    if (!item || !onUpdateItem) return;
    if (beneficiaries.length > 0) {
      const total =
        Math.round(beneficiaries.reduce((sum, b) => sum + (b.share_percentage ?? 0), 0) * 100) /
        100;
      if (total !== 100) {
        enqueueSnackbar("Allocations need to total 100%. Update to continue.", {
          variant: "error",
        });
        return;
      }
    }
    const updates: Record<string, unknown> = {};
    const isEstimatedNow = editForm.is_estimated === true;
    const newBalance = resolveBalance(
      isEstimatedNow,
      editForm.balance_estimate_range as string,
      String(editForm.balance ?? ""),
    );
    if (!isNaN(newBalance) && newBalance !== item.balance) updates.balance = newBalance;
    if (editForm.ownership !== (item.ownership ?? ""))
      updates.ownership = (editForm.ownership as string) || null;
    if (editForm.last_updated_date !== (item.last_updated_date ?? ""))
      updates.last_updated_date = (editForm.last_updated_date as string) || null;
    if (editForm.is_estimated !== (item.is_estimated ?? false))
      updates.is_estimated = editForm.is_estimated;
    if ((editForm.balance_estimate_range ?? "") !== (item.balance_estimate_range ?? ""))
      updates.balance_estimate_range = (editForm.balance_estimate_range as string) || null;
    if (editForm.item_type !== (item.item_type ?? ""))
      updates.item_type = (editForm.item_type as string) || null;
    const currentLast4 =
      (item.identifiers?.account_last4 as string) ?? item.plaid_account_mask ?? "";
    if (editForm.account_last4 !== currentLast4) {
      updates.identifiers = {
        ...(item.identifiers ?? {}),
        account_last4: (editForm.account_last4 as string) || undefined,
      };
    }
    const metaUpdates: Record<string, unknown> = {};
    const oldMeta = (item.metadata ?? {}) as Record<string, unknown>;
    for (const [k, v] of Object.entries(editForm)) {
      if (k.startsWith("meta_")) {
        const metaKey = k.slice(5);
        if (v !== (oldMeta[metaKey] ?? "")) metaUpdates[metaKey] = v || undefined;
      }
    }
    const hasPropertyAddressField =
      details?.fields.some((fld) => fld.fieldType === "propertyAddress") ?? false;
    if (hasPropertyAddressField && addressEdited) {
      const parts: Record<string, string> = {
        street_address: addressDraft.streetAddress.trim(),
        apartment: addressDraft.apartment.trim(),
        city: addressDraft.city.trim(),
        state: addressDraft.state.trim(),
        zip_code: addressDraft.zipCode.trim(),
      };
      for (const [key, value] of Object.entries(parts)) {
        if (value !== ((oldMeta[key] as string) ?? "")) metaUpdates[key] = value || undefined;
      }
    }

    const hasAddressField = details?.fields.some((fld) => fld.fieldType === "address") ?? false;
    if (hasAddressField) {
      const newAddress = buildAddress(addressDraft).trim() ? addressDraft : undefined;
      const oldAddressRaw = oldMeta.address as AddressParts | undefined;
      const normalize = (a: AddressParts | undefined) =>
        a ? JSON.stringify({ ...EMPTY_ADDRESS, ...a }) : "null";
      if (normalize(oldAddressRaw) !== normalize(newAddress)) {
        metaUpdates.address = newAddress;
      }
    }
    if (Object.keys(metaUpdates).length > 0) {
      updates.metadata = { ...oldMeta, ...metaUpdates };
    }
    if (Object.keys(updates).length > 0) {
      setIsSaving(true);
      try {
        await onUpdateItem(item.id, updates);
      } catch {
        enqueueSnackbar("Couldn't save changes. Please try again.", { variant: "error" });
        setIsSaving(false);
        return;
      }
      setIsSaving(false);
    }
    setIsEditing(false);
  };

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300",
          visible ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-label="Estate item details"
        aria-modal="true"
        className={cn(
          "fixed inset-0 z-50 flex flex-col bg-white shadow-lg transition-transform duration-300 ease-in-out",
          "sm:left-auto sm:right-0 sm:top-0 sm:bottom-0 sm:w-[853px]",
          visible ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex flex-1 flex-col overflow-y-auto">
          {item && (
            <SheetHeader
              item={item}
              details={details}
              dateOfDeath={person?.date_of_death}
              onClose={onClose}
              onRename={() => {
                setRenameValue(item.item_name);
                setIsRenameOpen(true);
              }}
              onChangeType={() => setIsChangeTypeOpen(true)}
              onDelete={() => setIsDeleteConfirmOpen(true)}
            />
          )}

          <hr className="mx-4 border-primary sm:mx-6" />

          {needsValue && item && onUpdateItem && (
            <>
              <AddValueSection
                key={item.id}
                itemId={item.id}
                classification={item.classification}
                valueLabel={details?.valueLabel ?? "Value"}
                onSave={onUpdateItem}
              />
              <hr className="mx-4 border-primary sm:mx-6" />
            </>
          )}

          <div className="px-4 py-4 sm:px-6 sm:py-6">
            <div className="mb-4 flex items-center justify-between">
              <h4>{isEditing ? "Edit details" : (details?.sectionTitle ?? "Details")}</h4>
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="md" onClick={handleEditCancel}>
                    Cancel
                  </Button>
                  <Button variant="brand" size="md" onClick={handleEditSave} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save changes"}
                  </Button>
                </div>
              ) : (
                <button
                  onClick={handleEditStart}
                  disabled={situsPending}
                  className="flex items-center gap-1.5 text-sm font-medium text-brand transition-colors hover:text-brand/80 disabled:opacity-50"
                >
                  Edit fields
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {isEditing && item ? (
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {details?.fields.map((field) => {
                    if (field.fieldType === "identifier") {
                      const val = editForm.account_last4 as string;
                      return (
                        <div key={field.label}>
                          <p className="mb-1 text-xs text-secondary">{field.label}</p>
                          <div className="relative">
                            <Input
                              value={identifierMasked && val ? "\u2022\u2022\u2022\u2022" : val}
                              onChange={(e) => {
                                setIdentifierMasked(false);
                                setEditForm((f) => ({ ...f, account_last4: e.target.value }));
                              }}
                              onFocus={() => setIdentifierMasked(false)}
                              placeholder="0000"
                              maxLength={4}
                              className="pr-9 text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => setIdentifierMasked((m) => !m)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-tertiary hover:text-primary"
                              aria-label={identifierMasked ? "Show number" : "Hide number"}
                            >
                              {identifierMasked ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    }

                    if (field.fieldType === "ownership") {
                      return (
                        <div key={field.label}>
                          <p className="mb-1 text-xs text-secondary">Ownership type</p>
                          <Select
                            value={editForm.ownership as string}
                            onValueChange={(val) => setEditForm((f) => ({ ...f, ownership: val }))}
                          >
                            <SelectTrigger className="text-sm">
                              <SelectValue placeholder="Not set" />
                            </SelectTrigger>
                            <SelectContent>
                              {OWNERSHIP_TYPES.map((t) => (
                                <SelectItem key={t.slug} value={t.slug}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    }

                    if (field.fieldType === "value") {
                      const isDebtItem = item.classification === "debt";
                      const balInput = currencyInput(editForm.balance as string, (val) =>
                        setEditForm((f) => ({ ...f, balance: val })),
                      );
                      return (
                        <div key={field.label}>
                          <p className="mb-1 text-xs text-secondary">{details.valueLabel}</p>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-tertiary">
                              {isDebtItem ? "-$" : "$"}
                            </span>
                            <Input
                              value={balInput.displayValue}
                              onChange={balInput.handleChange}
                              disabled={editForm.is_estimated === true}
                              className={isDebtItem ? "pl-9 text-sm" : "pl-7 text-sm"}
                            />
                          </div>
                        </div>
                      );
                    }

                    if (field.fieldType === "address" || field.fieldType === "propertyAddress") {
                      return (
                        <div key={field.label} className="sm:col-span-2">
                          <p className="mb-2 text-xs text-secondary">{field.label}</p>
                          <AddressPicker
                            value={addressDraft}
                            onChange={(next) => {
                              setAddressDraft(next);
                              setAddressEdited(true);
                            }}
                            stateFormat={
                              field.fieldType === "propertyAddress" ? "abbreviation" : "label"
                            }
                          />
                        </div>
                      );
                    }

                    return (
                      <div key={field.label}>
                        <p className="mb-1 text-xs text-secondary">{field.label}</p>
                        <p className="text-sm">{field.value}</p>
                      </div>
                    );
                  })}
                  <div>
                    <p className="mb-1 text-xs text-secondary">Value as of date</p>
                    <DatePicker
                      value={
                        (editForm.last_updated_date as string)
                          ? new Date((editForm.last_updated_date as string) + "T00:00:00")
                          : undefined
                      }
                      onChange={(date) =>
                        setEditForm((f) => ({
                          ...f,
                          last_updated_date: formatPersonDateForSave(date) ?? "",
                        }))
                      }
                      placeholder="Pick a date"
                    />
                  </div>
                </div>

                {showBalanceEstimate && (
                  <BalanceEstimateSection
                    balanceUnknown={editForm.is_estimated as boolean}
                    estimateRange={editForm.balance_estimate_range as string}
                    onBalanceUnknownChange={(checked) =>
                      setEditForm((f) => ({
                        ...f,
                        is_estimated: checked,
                        ...(checked ? { balance: "" } : {}),
                      }))
                    }
                    onEstimateRangeChange={(key) =>
                      setEditForm((f) => ({ ...f, balance_estimate_range: key }))
                    }
                  />
                )}

                {item.classification !== "debt" && (
                  <BeneficiaryEditor
                    beneficiaries={beneficiaries}
                    contacts={externalContacts}
                    localShares={localShares}
                    onLocalShareChange={(id, value) =>
                      setLocalShares((s) => ({ ...s, [id]: value }))
                    }
                    onShareBlur={(id) => {
                      const raw = localShares[id];
                      if (raw === undefined) return;
                      const val = parseInt(raw, 10);
                      updateBeneficiary({
                        id,
                        estate_item_id: item!.id,
                        share_percentage: isNaN(val) ? null : val,
                      });
                      setLocalShares((s) => {
                        const next = { ...s };
                        delete next[id];
                        return next;
                      });
                    }}
                    onAdd={addBeneficiary}
                    onRemove={removeBeneficiary}
                    itemId={item.id}
                  />
                )}
              </div>
            ) : (
              <div
                className={cn(
                  "grid gap-6 grid-cols-1",
                  (details?.fields.length ?? 0) +
                    (item?.classification !== "debt" && beneficiaries.length > 0 ? 1 : 0) ===
                    3
                    ? "sm:grid-cols-3"
                    : "sm:grid-cols-2",
                )}
              >
                {details?.fields.map((field) => (
                  <div key={field.label}>
                    <p className="mb-1 text-xs text-secondary">{field.label}</p>
                    <p className="text-sm font-medium">{field.value}</p>
                  </div>
                ))}
                {item?.classification !== "debt" && beneficiaries.length > 0 && (
                  <div>
                    <p className="mb-1 text-xs text-secondary">Beneficiaries</p>
                    {beneficiaries.map((b) => {
                      const share =
                        b.share_percentage != null
                          ? `${b.share_percentage}%`
                          : b.numerator != null && b.denominator != null
                            ? `${b.numerator}/${b.denominator}`
                            : null;
                      return (
                        <p key={b.id} className="text-sm font-medium">
                          {b.contact_name}
                          {share && <span className="ml-2 font-medium text-tertiary">{share}</span>}
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {linkedSecuredDebts &&
            linkedSecuredDebts.length > 0 &&
            item &&
            (() => {
              const totalDebtBalance = linkedSecuredDebts.reduce((sum, d) => sum + d.balance, 0);
              const grouped = groupDebtsByType(linkedSecuredDebts);
              return Object.entries(grouped).map(([type, debts]) => (
                <LinkedMortgageSection
                  key={type}
                  debts={debts}
                  propertyBalance={item.balance}
                  totalDebtBalance={totalDebtBalance}
                  onSelect={onSelectItem}
                />
              ));
            })()}

          {linkedProperty && (
            <LinkedPropertySection property={linkedProperty} onSelect={onSelectItem} />
          )}

          <hr className="mx-4 border-primary sm:mx-6" />

          {/* Notes */}
          {item?.notes && item.notes !== "__MARKED_AS_NONE__" && (
            <>
              <div className="px-4 py-4 sm:px-6 sm:py-6">
                <div className="mb-3 flex items-center justify-between">
                  <h4>Notes</h4>
                  <p className="text-sm text-secondary">Added by Elayne</p>
                </div>
                <p className="whitespace-pre-line text-sm text-secondary">{item.notes}</p>
              </div>
              <hr className="mx-4 border-primary sm:mx-6" />
            </>
          )}

          <RelatedDocsSection
            docRefs={docRefs}
            isLoading={docsLoading}
            onDocumentClick={
              onDocumentClick
                ? (ref) =>
                    onDocumentClick({
                      id: ref.document_id,
                      name: ref.display_name || ref.file_name || "Document",
                      createdAt: ref.document_created_at,
                    })
                : undefined
            }
          />

          <hr className="mx-4 border-primary sm:mx-6" />

          <ActivityLogSection
            entries={allEntries}
            isLoading={activityLoading}
            isPaidUser={isPaidUser}
            personName={person?.first_name || "the estate"}
            onUpgrade={onUpgrade}
          />
        </div>
      </div>

      {item && (
        <ChangeItemTypeDialog
          open={isChangeTypeOpen}
          onOpenChange={setIsChangeTypeOpen}
          currentCategory={item.category}
          currentItemType={item.item_type ?? undefined}
          isUpdating={isChangingType}
          onConfirm={async (newCategory, newItemType, classification) => {
            if (!onUpdateItem) return;
            setIsChangingType(true);
            try {
              await onUpdateItem(item.id, {
                category: newCategory,
                item_type: newItemType || null,
                classification,
              });
              setIsChangeTypeOpen(false);
            } catch {
              enqueueSnackbar("Couldn't change type. Please try again.", { variant: "error" });
            } finally {
              setIsChangingType(false);
            }
          }}
        />
      )}

      <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename item</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label>Name</Label>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Item name"
              autoFocus
            />
          </div>
          <DialogFooter
            onSubmit={async () => {
              if (!item || !onUpdateItem || !renameValue.trim()) return;
              setIsRenaming(true);
              try {
                await onUpdateItem(item.id, {
                  item_name: renameValue.trim(),
                  is_custom_name: true,
                });
                setIsRenameOpen(false);
              } catch {
                enqueueSnackbar("Couldn't rename item. Please try again.", { variant: "error" });
              } finally {
                setIsRenaming(false);
              }
            }}
            submitText="Save"
            disabled={!renameValue.trim()}
            loading={isRenaming}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        isLoading={isDeleting}
        onConfirm={async () => {
          if (!item || !onDeleteItem) return;
          setIsDeleting(true);
          try {
            await onDeleteItem(item.id);
            setIsDeleteConfirmOpen(false);
            onClose();
          } catch {
            enqueueSnackbar("Couldn't remove item. Please try again.", { variant: "error" });
          } finally {
            setIsDeleting(false);
          }
        }}
        title="Remove from Estate"
        description="Are you sure you want to remove this item from the estate? This action cannot be undone."
      />
    </>
  );
};
